import { useState, useRef } from 'react';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button
} from '@mui/material';

interface IAutoStartInfo {
    shown: boolean;
}

/**
 * 自启动权限引导Hook
 * 用于在首次启用跌倒检测时显示自启动权限说明对话框
 */
export function useAutoStartPermission() {
    const [autoStartDialogOpen, setAutoStartDialogOpen] = useState(false);
    const autoStartCallbackRef = useRef<() => void>();

    // 检查是否需要显示自启动引导
    function checkAutoStartPermission(callback: () => void) {
        const def: IAutoStartInfo = { shown: false };
        const res = autoJsStoreUtils.read<IAutoStartInfo>('auto_start_permission_shown', def);
        const value: IAutoStartInfo = res && 'shown' in res ? res : def;

        // 已经显示过了
        if (value.shown) {
            callback();
            return;
        }

        // 需要弹出自启动引导
        autoStartCallbackRef.current = callback;
        setAutoStartDialogOpen(true);
    }

    // 处理自启动引导对话框
    const handleAutoStartDialog = (agreed: boolean) => {
        console.log('[自启动引导] 用户选择:', agreed ? '同意' : '暂不开启');

        // 保存用户选择
        try {
            const script = `
                var EmergencySettings = Packages.com.fanfanlo.droid.utils.EmergencySettings;
                var context = com.fanfanlo.lib.mc.LibMainModel.instance.application;
                EmergencySettings.setAutoStartEnabled(context, ${agreed});
                EmergencySettings.setAutoStartPermissionShown(context);
            `;
            autoWebViewJs.callScript(script);
            console.log('[自启动引导] 设置已保存');
        } catch (error) {
            console.error('[自启动引导] 保存设置失败:', error);
        }

        // 标记已显示
        autoJsStoreUtils.write('auto_start_permission_shown', { shown: true });

        setAutoStartDialogOpen(false);

        // 继续启动监控
        if (autoStartCallbackRef.current) {
            autoStartCallbackRef.current();
        }
    };

    // 自启动引导对话框组件
    const AutoStartDialog = () => (
        <Dialog
            open={autoStartDialogOpen}
            onClose={() => handleAutoStartDialog(false)}
            aria-labelledby="auto-start-dialog-title"
            aria-describedby="auto-start-dialog-description"
        >
            <DialogTitle id="auto-start-dialog-title">
                {"开机自启动权限说明"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="auto-start-dialog-description">
                    为了确保紧急保护功能能够持续工作，我们需要您的授权：
                    <br /><br />
                    <strong>【开机自启动功能】</strong>
                    <br /><br />
                    <strong>用途：</strong>
                    <br />
                    • 设备重启后自动启动保护服务
                    <br />
                    • 确保跌倒检测等功能持续运行
                    <br />
                    • 在紧急情况下及时提供帮助
                    <br /><br />
                    <strong>您的控制权：</strong>
                    <br />
                    • 您可以随时在设置中关闭此功能
                    <br />
                    • 关闭后不影响应用的其他功能
                    <br />
                    • 我们承诺仅用于安全保护，不会用于广告推送
                    <br /><br />
                    是否允许应用在开机时自动启动？
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => handleAutoStartDialog(false)}>暂不开启</Button>
                <Button onClick={() => handleAutoStartDialog(true)} autoFocus variant="contained">
                    允许
                </Button>
            </DialogActions>
        </Dialog>
    );

    return {
        checkAutoStartPermission,
        AutoStartDialog
    };
}
